import { RefObject, useEffect, useLayoutEffect, useState } from 'react'
import { DropdownProps } from './Dropdown'

export const useDropdownPosition = (
    containerRef: RefObject<HTMLDivElement> | undefined,
    dropdownRef: RefObject<HTMLDivElement> | undefined,
    deps: unknown[] = []
) => {
    const [horizontalAlign, setHorizontalAlign] =
        useState<DropdownProps['horizontalAlign']>('left')
    const [verticalAlign, setVerticalAlign] =
        useState<DropdownProps['verticalAlign']>('bottom')
    const [containerSize, setContainerSize] = useState<{
        width: number
        height: number
    } | null>(null)

    useLayoutEffect(() => {
        if (containerRef?.current && dropdownRef?.current) {
            const dropdownBox = dropdownRef.current.getBoundingClientRect()
            const dropdownContent =
                dropdownRef.current.querySelector('.dropdown-content')

            if (!dropdownContent) {
                return
            }

            const dropdownContentBox = dropdownContent.getBoundingClientRect()
            const containerBox = containerRef.current.getBoundingClientRect()

            let newHorizontalAlign: DropdownProps['horizontalAlign'] = 'left'
            if (
                dropdownBox.left + dropdownContentBox.width >
                containerBox.right
            ) {
                newHorizontalAlign = 'right'
            } else if (
                dropdownBox.right - dropdownContentBox.width <
                containerBox.left
            ) {
                newHorizontalAlign = 'left'
            }

            const dropdownContentStyle =
                window.getComputedStyle(dropdownContent)
            const dropdownContentMarginTop = parseInt(
                dropdownContentStyle.marginTop,
                10
            )

            let newVerticalAlign: DropdownProps['verticalAlign'] = 'bottom'
            if (
                dropdownBox.bottom +
                    dropdownContentBox.height +
                    dropdownContentMarginTop >
                containerBox.bottom
            ) {
                newVerticalAlign = 'top'
            } else if (
                dropdownBox.top - dropdownContentBox.height <
                containerBox.top
            ) {
                newVerticalAlign = 'bottom'
            }

            setHorizontalAlign((prev) =>
                prev !== newHorizontalAlign ? newHorizontalAlign : prev
            )
            setVerticalAlign((prev) =>
                prev !== newVerticalAlign ? newVerticalAlign : prev
            )
        }
    }, [containerRef, dropdownRef, containerSize, ...deps])

    useEffect(() => {
        if (!containerRef?.current) return

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerSize({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                })
            }
        })

        resizeObserver.observe(containerRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [containerRef])

    return { horizontalAlign, verticalAlign }
}
